import { ref, nextTick, toRefs, reactive, set } from 'vue'
import moment from 'moment'
import { getPlanLaunchTimeByIds, updatePlanLaunchDateTime, retryPlanLaunchDateTime } from '@/api/ptom.data.model'
import { MEDIAS } from '@/common/js/constants'
import { ALL_DAYS_SCHEDULE, KUAISHOU_ALL_DAYS_SCHEDULE, TASK_TYPE, TIME_TYPE, DATE_POS, DATE_TYPE } from './consts/index.js'

function getConfigForm() {
  return {
    taskType: 1, // 1: 时间段 2: 日期
    timeType: 1, // 1: 选择时间段 2: 全天投放
    dateType: 1, // 1: 选择日期 2: 长期投放
    timeSchedule: '',
    startDate: '',
    endDate: ''
  }
}

const formModel = reactive({
  confirmLoading: false,
  showResults: false, // 显示提交后的结果
  configForm: getConfigForm()
})

const tableModel = reactive({
  tableLoading: false,
  tableErrorMsg: '',
  showScheduleModifyModal: false,
  tableInitSuccess: false
})

function removeErrorMsg() {
  tableModel.tableErrorMsg = ''
}
function registerDialogEvent() {
  document.querySelector('.modify-datetime-batch').addEventListener('mouseup', removeErrorMsg)
}

/**
 * @description: 表格相关操作
 * @param {*} emit
 * @param {*} tableData
 * @return {*}
 */
export const useTableActions = (emit, tableData) => {
  let tableRowModifyProps = null

  const showRowModifyBtn = (row) => {
    // 为提交前可显示；
    if (!formModel.showResults && !row.hasSameProject) return true
    // 提交后如果是失败可显示；
    if (row.dateTimeResult?.projectLaunchDateMsg) return true
    return false
  }

  const tableRowClassName = ({ row }) => {
    if (row.hasSameProject) return 'row-disabled'
    return ''
  }

  /**
   * @description: 移除一行
   * @param {*} index
   * @return {*}
   */
  const onTableRowRemove = (index) => {
    const newRows = tableData.value.filter((item, _index) => _index !== index)
    emit('update:rows', newRows)
  }

  /**
   * @description: 每次打开弹窗获取表格投放日期数据合并到原始表格
   * @param {*} tableRows
   */
  const initTableRows = async (tableRows) => {
    const params = tableRows.map((row) => ({
      planId: row.planId,
      mediaId: row.mediaId,
      adAccountId: row.adAccountId
    }))

    tableModel.tableLoading = true
    try {
      // 根据计划id获取每个计划的投放时间、日期
      const launchDateTimeList = await getPlanLaunchTimeByIds(params)
      // 查询失败/被去重时会返回 false 或 Symbol，统一兜底成空数组，避免下面 .find 抛错导致表格整列不渲染
      const launchList = Array.isArray(launchDateTimeList) ? launchDateTimeList : []

      const newTables = tableRows.map((row) => {
        const launchDateTime = launchList.find((i) => i.planId === row.planId) || {}
        const { currentLaunchTimeSeries, launchStartTime, launchEndTime } = launchDateTime
        let formatedSchedulesText = '全天投放'

        if (row.mediaId === MEDIAS.TOUTIAO || row.mediaId === MEDIAS.GDT) {
          if (currentLaunchTimeSeries && typeof currentLaunchTimeSeries === 'string') {
            if (
              currentLaunchTimeSeries.length === ALL_DAYS_SCHEDULE.length &&
              currentLaunchTimeSeries.includes('0') &&
              currentLaunchTimeSeries.includes('1')
            ) {
              formatedSchedulesText = normalScheduleTransformer(currentLaunchTimeSeries)
            }
          }
        }

        if (row.mediaId === MEDIAS.KUAISHOU) {
          if (currentLaunchTimeSeries && typeof currentLaunchTimeSeries === 'string') {
            if (
              currentLaunchTimeSeries.length === KUAISHOU_ALL_DAYS_SCHEDULE.length &&
              currentLaunchTimeSeries.includes('0') &&
              currentLaunchTimeSeries.includes('1')
            ) {
              formatedSchedulesText = normalScheduleTransformer(currentLaunchTimeSeries, 24)
            }
          }
        }

        if (row.mediaId === MEDIAS.BAIDU) {
          if (currentLaunchTimeSeries && typeof currentLaunchTimeSeries === 'string') {
            // currentLaunchTimeSeries 非合法 JSON 时不能让整个 init 抛错
            try {
              const schedules = baiduScheduleTransformer(JSON.parse(currentLaunchTimeSeries))
              formatedSchedulesText = normalScheduleTransformer(schedules, 24)
            } catch (e) {
              console.log('baidu schedule parse error output: ', e)
            }
          }
        }

        const newRow = {
          ...row,
          launchDateTime,
          formatedSchedulesText
        }

        // 长期投放/未开始等情况下 launchStartTime 可能为空，需兜底，否则 .split 抛错会让整个表格不渲染
        const startDate = launchStartTime ? launchStartTime.split(' ')[0] : ''
        const endDate = launchEndTime ? launchEndTime.split(' ')[0] : '长期投放'

        newRow.currLaunchDate = [startDate, endDate]

        return newRow
      })

      // 已进入结果态后，不再用打开时查到的投放时间覆盖掉带结果的行
      if (formModel.showResults) return

      emit('update:rows', newTables)

      nextTick(registerDialogEvent)
    } catch (e) {
      console.log('getPlanLaunchTime error output: ', e)
    } finally {
      // 无论查询成功与否都放开表格渲染，避免提交后结果态只剩标题
      tableModel.tableLoading = false
      tableModel.tableInitSuccess = true
    }
  }

  /**
   * @description: 单条修改时段
   * @param {*} row
   * @param {*} rowIndex
   */
  const onTableRowScheduleModify = (row, rowIndex) => {
    tableRowModifyProps = { row, rowIndex }
    tableModel.showScheduleModifyModal = true
  }

  const onTableRowDateModify = (row, date, type) => {
    const { updateStartDateTypeRadio, updateEndDateTypeRadio, updateStartDateText, updateEndDateText } = row
    if (type === DATE_POS.START) {
      if (updateEndDateText && date >= updateEndDateText) {
        tableModel.tableErrorMsg = '开始日期要小于结束日期！'
        return
      }
    } else if (type === DATE_POS.END) {
      if (updateStartDateText && date <= updateStartDateText) {
        tableModel.tableErrorMsg = '结束日期要大于开始日期！'
        return
      }
    }
    const newRows = tableData.value.map((item, index) => {
      if (row.planId === item.planId) {
        if (type === DATE_POS.START) {
          // 修改后开始时间相关参数
          if (updateStartDateTypeRadio === 1) {
            return { ...row, updateStartDateText: date, updateStartDateValue: date }
          }
        } else if (type === DATE_POS.END) {
          // 修改后结束时间相关参数
          if (updateEndDateTypeRadio === 1) {
            return { ...row, updateEndDateText: date, updateEndDateValue: date }
          }

          if (updateEndDateTypeRadio === 0) {
            return { ...row, updateEndDateText: '长期投放' }
          }
        }
      }
      return item
    })

    emit('update:rows', newRows)
  }

  const onScheduleModifyConfirm = (timeSchedule) => {
    const newRow = setTableScheduleDisplayText(tableRowModifyProps.row, timeSchedule)
    const newRows = tableData.value.map((item, index) => {
      if (index === tableRowModifyProps.rowIndex) {
        return newRow
      }
      return item
    })

    emit('update:rows', newRows)
  }

  /**
   * @description: 清除列
   */
  const onColRemoveAll = () => {
    const newRows = tableData.value.map((row) => {
      return { ...row, updateDateText: '', updateFormatedSchedules: '', updateFormatedSchedulesText: '' }
    })

    formModel.configForm.timeSchedule = ''

    emit('update:rows', newRows)
  }

  return {
    ...toRefs(tableModel),
    onTableRowRemove,
    tableRowClassName,
    initTableRows,
    showRowModifyBtn,
    onTableRowScheduleModify,
    onTableRowDateModify,
    onScheduleModifyConfirm,
    onColRemoveAll
  }
}

/**
 * @description: 配置表单相关操作
 * @return {*}
 */
export const useConfigForm = (emit, tableData) => {
  /**
   * @description: 选择时间区间时，获取头条、腾讯广告媒体半小时粒度的投放时间、获取快手媒体小时粒度的投放时间、获取百度媒体小时粒度的排除投放时间
   * @param {*} e
   * @param {*} v
   * @return {
   *  ...
   *  updateFormatedSchedulesText: [string, array] 用于表格显示的修改后的时间段
   *  updateFormatedSchedules: [string 用于头条、腾讯广告、快手的时间段提交, object 用于百度的排除投放时间段提交]
   * }
   */
  function onWeektimeChange(e) {
    const newRows = tableData.value.map((row) => setTableScheduleDisplayText(row, e))
    emit('update:rows', newRows)
  }

  /**
   * @description: 切换类型
   * @return {*}
   */
  function onTaskTypeChange(val) {
    if (val === TASK_TYPE.DATE) {
      const newRows = tableData.value.map((row) => {
        return { ...row, updateStartDateTypeRadio: 1, updateEndDateTypeRadio: 1 }
      })
      emit('update:rows', newRows)
    }
  }

  /**
   * @description: 切换时间段类型
   * @return {*}
   */
  function onTimeTypeChange(val) {
    // 切换到全天投放时间段
    if (val === TIME_TYPE.ALL_DAY) {
      const newRows = tableData.value.map((row) => {
        return { ...row, updateFormatedSchedulesText: '全天投放' }
      })

      formModel.configForm.timeSchedule = ''
      emit('update:rows', newRows)
    }
    // 切换到选择时间段
    if (val === TIME_TYPE.TIME_RANGE) {
      const newRows = tableData.value.map((row) => {
        return { ...row, updateFormatedSchedulesText: '' }
      })
      emit('update:rows', newRows)
    }
  }

  /**
   * @description: 切换投放日期类型
   * @param {*} val
   * @return {*}
   */
  function onDateTypeChange(val) {
    const newRows = tableData.value.map((row) => {
      const { updateEndDateValue } = row
      const updateEndDateText = val === DATE_TYPE.LONG_TERM ? '长期投放' : updateEndDateValue
      const updateEndDateTypeRadio = val === DATE_TYPE.LONG_TERM ? 0 : 1
      return { ...row, updateEndDateText, updateEndDateTypeRadio }
    })
    emit('update:rows', newRows)
  }

  /**
   * @description: 选择日期
   * @param {*} date
   */
  function onDateChange(date, type) {
    const { startDate, endDate } = formModel.configForm
    if (type === DATE_POS.START) {
      if (endDate && date >= endDate) {
        formModel.configForm.startDate = ''
        tableModel.tableErrorMsg = '开始日期要小于结束日期！'
        return
      }
    } else if (type === DATE_POS.END) {
      if (startDate && date <= startDate) {
        formModel.configForm.endDate = ''
        tableModel.tableErrorMsg = '结束日期要大于开始日期！'
        return
      }
    }
    const newRows = tableData.value.map((row) => {
      const newRow = { ...row }

      if (type === DATE_POS.START && [MEDIAS.GDT, MEDIAS.KUAISHOU, MEDIAS.BAIDU].includes(row.mediaId)) {
        newRow.updateStartDateText = date

        // popover 单条修改日期和类型回显
        if (date) {
          newRow.updateStartDateValue = date
          newRow.updateStartDateTypeRadio = 1
        } else {
          newRow.updateStartDateValue = ''
          newRow.updateStartDateTypeRadio = 0
        }
      } else if (type === DATE_POS.END) {
        newRow.updateEndDateText = date

        // popover 单条修改日期和类型回显
        if (date) {
          newRow.updateEndDateValue = date
          newRow.updateEndDateTypeRadio = 1
        } else {
          newRow.updateEndDateValue = ''
          newRow.updateEndDateTypeRadio = 0
        }
      }
      return newRow
    })
    emit('update:rows', newRows)
  }

  /**
   * @description: 生成提交参数。且进行校验
   * @return {*}
   */
  function buildSubmitParams() {
    const { taskType, timeType } = formModel.configForm

    const params = tableData.value

      // 过滤掉同项目广告
      .filter((row) => !row.hasSameProject)
      .map((row, index) => {
        const { mediaId, planId, adAccountId } = row

        const paramRow = {
          planId,
          mediaId,
          adAccountId
        }

        // 时间段
        if (taskType === TASK_TYPE.TIME) {
          if (timeType === TIME_TYPE.TIME_RANGE) {
            if (!row.updateFormatedSchedules) {
              throw new Error(`序号：${index + 1}行投放时间段不能为空！`)
            }

            if ([MEDIAS.TOUTIAO, MEDIAS.GDT, MEDIAS.KUAISHOU].includes(mediaId)) {
              paramRow.updateLaunchAfterTimeSeries = row.updateFormatedSchedules
            }

            if (mediaId === MEDIAS.BAIDU) {
              // 数组元素个数限制：每天可设置最多12个推广暂停时间，每周可设置最多84个推广暂停时间
              if (Object.values(row.updateFormatedSchedules).length > 84) {
                throw new Error(`序号：${index + 1}行投放时间段超过限制！`)
              }
              paramRow.baiDuUpdateLaunchAfterPauseTimeSeries = JSON.stringify(row.updateFormatedSchedules)
            }
          }

          // 全天投放
          if (timeType === TIME_TYPE.ALL_DAY) {
            if (mediaId === MEDIAS.BAIDU) {
              paramRow.baiDuUpdateLaunchAfterPauseTimeSeries = '1'
            }

            if ([MEDIAS.TOUTIAO, MEDIAS.GDT].includes(mediaId)) {
              paramRow.updateLaunchAfterTimeSeries = ALL_DAYS_SCHEDULE
            }

            if ([MEDIAS.KUAISHOU].includes(mediaId)) {
              paramRow.updateLaunchAfterTimeSeries = KUAISHOU_ALL_DAYS_SCHEDULE
            }
          }
        }
        // 日期
        if (taskType === TASK_TYPE.DATE) {
          if (!row.updateStartDateText && !row.updateEndDateText) {
            throw new Error(`序号：${index + 1}行开始日期和结束日期不能都为空！`)
          }
          // updateEndDateText 可为日期字符串或'长期投放'
          paramRow.updateAfterLaunchEndTime = row.updateEndDateText
          if (row.updateStartDateText && moment(row.updateStartDateText).isValid()) {
            paramRow.updateLaunchStartTime = row.updateStartDateText
            if ([MEDIAS.BAIDU].includes(mediaId)) {
              paramRow.updateLaunchStartTime = row.updateStartDateText + ' 00:00:00'
            }
          }
          if (row.updateEndDateText && moment(row.updateEndDateText).isValid()) {
            if ([MEDIAS.TOUTIAO, MEDIAS.BAIDU].includes(mediaId)) {
              paramRow.updateAfterLaunchEndTime = row.updateEndDateText + ' 00:00:00'
            }
          }
        }

        return paramRow
      })

    return params
  }

  /**
   * @description: 确认提交修改
   * @return {*}
   */
  async function onBatchModifyConfirm() {
    try {
      const params = buildSubmitParams()
      formModel.confirmLoading = true
      const results = await updatePlanLaunchDateTime(params)

      if (!results) return

      const newTables = tableData.value.map((row) => {
        const rowResult = results.find((result) => result.planId === row.planId)
        return {
          ...row,
          dateTimeResult: rowResult
        }
      })

      formModel.confirmLoading = false

      // 显示结果内容
      formModel.showResults = true
      emit('update:rows', newTables)
      emit('refresh')
    } catch (e) {
      console.log('onBatchModifyConfirm eeeeeeeeee output: ', e)
      tableModel.tableErrorMsg = e.message
      formModel.confirmLoading = false
    }
  }

  function onFailedRowRetry(row) {
    const [params] = buildSubmitParams().filter((p) => p.planId === row.planId) || []

    if (params) {
      set(row, 'retryLoading', true)
      retryPlanLaunchDateTime({ ...params, id: row.dateTimeResult.id, reqId: row.dateTimeResult.reqId })
        .then((data) => {
          if (data) {
            const newRows = tableData.value.map((item) => {
              if (item.planId === row.planId) {
                return { ...item, retryLoading: false, dateTimeResult: data }
              }
              return item
            })

            emit('update:rows', newRows)
          }
        })
        .finally(() => {
          set(row, 'retryLoading', false)
        })
    }
  }

  return {
    ...toRefs(formModel),
    onDateChange,
    onWeektimeChange,
    onTaskTypeChange,
    onTimeTypeChange,
    onDateTypeChange,
    onBatchModifyConfirm,
    onFailedRowRetry
  }
}

export const useDialog = (dialogVisible) => {
  function onBatchModifyCancel() {
    formModel.configForm = getConfigForm()

    dialogVisible.value = false
    formModel.showResults = false
    tableModel.tableInitSuccess = false
    document.querySelector('.modify-datetime-batch').removeEventListener('mouseup', removeErrorMsg)
  }
  return { onBatchModifyCancel }
}

/**
 * @description: 获取快手、百度支持的以小时为最小粒度的投放时间
 * @param {*} schedules
 * @return {*}
 */
function getWeekHourList(schedules) {
  const list = []
  for (let i = 0; i < schedules.length; i++) {
    if (i % 2 === 0) {
      // 舍弃掉半小时的数据
      const isfull = +schedules[i] === 1 && +schedules[i + 1] === 1
      if (isfull) list.push(1)
      else list.push(0)
    }
  }
  return list.join('')
}

/**
 * @description: 百度媒体的投放排除时间段
 * @description: 生成百度的投放排除时间段 schedule https://dev2.baidu.com/content?sceneType=0&pageId=101246&nodeId=107&subhead=
 * @param {*} list
 * @return {*}
 */
function getBaiduExcludeList(list) {
  const baiduSchedule = []
  for (let i = 0; i < list.length; i++) {
    if (i % 24 === 0) {
      const rangeString = list.slice(i, i + 24)
      const regexp = /(0+)/g // 匹配连续的0
      const matches = [...rangeString.matchAll(regexp)]

      matches.forEach((match) => {
        const startIndex = match.index
        // 结束索引等于开始索引加上匹配到的字符长度
        const endIndex = startIndex + match[0].length

        baiduSchedule.push({ weekDay: i / 24 + 1, startHour: startIndex, endHour: endIndex })
      })
    }
  }

  return baiduSchedule
}

/**
 * @description: 四大媒体的投放时间段转化成文本显示
 * @param {*} normalSchedule
 * @param {*} needle 默认半小时粒度，一天48个半小时，头条腾讯广告=48，快手百度=24
 * @return {*}
 */
export function normalScheduleTransformer(schedule, needle = 48) {
  const weekScheduleTextObj = {}
  for (let i = 0; i < schedule.length; i++) {
    if (i % needle === 0) {
      const rangeString = schedule.slice(i, i + needle)
      const regexp = /(1+)/g // 匹配连续的1
      const matches = [...rangeString.matchAll(regexp)]
      const weekdayIndex = i / needle + 1

      if (!weekScheduleTextObj[weekdayIndex]) weekScheduleTextObj[weekdayIndex] = []

      matches.forEach((match) => {
        const startIndex = match.index
        // 结束索引等于开始索引加上匹配到的字符长度
        const endIndex = startIndex + match[0].length

        weekScheduleTextObj[weekdayIndex].push({
          // needle / 24 把半小时转化成小时
          startTime: numberToTime(startIndex / (needle / 24)),
          endTime: numberToTime(endIndex / (needle / 24))
        })
      })
    }
  }

  return Object.values(weekScheduleTextObj).map((range, index) => {
    const rangeText = range.map(({ startTime, endTime }) => `${startTime}~${endTime}`).join('、')
    return {
      id: index,
      week: '星期' + ['一', '二', '三', '四', '五', '六', '日'][index],
      value: rangeText
    }
  })
}

/**
 * @description: 转化成 00:00 格式
 * @param {*} number
 * @return {*}
 */
function numberToTime(number) {
  const numberText = number < 10 ? '0' + number : String(number)
  if (numberText.includes('.5')) {
    return numberText.replace('.5', ':30')
  } else {
    return numberText + ':00'
  }
}

/**
 * @description: 百度媒体的【排除】投放时间段json转换成'01'字符串类型的【选择】时间段
 * @return {*}
 */
function baiduScheduleTransformer(baiduSchedule) {
  const schedule = {}

  for (let i = 0; i < baiduSchedule.length; i++) {
    const { weekDay, startHour, endHour } = baiduSchedule[i]

    // 默认生成一个全选的投放时间段，下面会根据排除时间进行修改
    if (!schedule[weekDay]) schedule[weekDay] = Array.from({ length: 24 }, (_, i) => 1)

    // 排除的投放时间段
    const excludeTimeRange = Array.from({ length: endHour - startHour }, (_, i) => startHour + i)

    for (let j = 0; j < schedule[weekDay].length; j++) {
      // 如果排除时间段包含当前索引，则将投放时间段置为0
      if (excludeTimeRange.includes(j)) {
        schedule[weekDay][j] = 0
      }
    }
  }
  return Object.values(schedule).flat().join('')
}

/**
 * @description: 根据媒体区分显示表格修改后的时间段数据
 * @param {*} row 行数据
 * @param {*} e 修改后的时间段
 * @return {*}
 */
function setTableScheduleDisplayText(row, e) {
  const newRow = { ...row }

  if (!e) {
    newRow.updateFormatedSchedulesText = ''
    newRow.updateFormatedSchedules = ''
    return newRow
  }

  if (row.mediaId === MEDIAS.TOUTIAO || row.mediaId === MEDIAS.GDT) {
    newRow.updateFormatedSchedulesText = normalScheduleTransformer(e)
    newRow.updateFormatedSchedules = e
  }

  if (row.mediaId === MEDIAS.KUAISHOU || row.mediaId === MEDIAS.BAIDU) {
    // 快手、百度的投放时间段
    const schedule = getWeekHourList(e)
    newRow.updateFormatedSchedulesText = normalScheduleTransformer(schedule, 24)
    if (row.mediaId === MEDIAS.KUAISHOU) {
      newRow.updateFormatedSchedules = schedule
    }

    if (row.mediaId === MEDIAS.BAIDU) {
      newRow.updateFormatedSchedules = getBaiduExcludeList(schedule)
    }
  }

  return newRow
}
